/**
 * useOpenUserDetail — click-to-user-detail controller (v1.4 L5, L6).
 *
 * Originally (H2) this was a URL-backed modal: `openUserDetail(id)` set
 * `?userDetail=<id>` on the current location and `<UserDetail />` mounted
 * a dialog. Users complained the modal was "too cropped, where are the
 * charts?" — the fullscreen `/user/:username` drill-down page already
 * exists (Phase 4 Plan 09) with 6 KPI cards + 3 charts + top lists +
 * activity + safety refusals + feedback timeline, so `openUserDetail`
 * now just navigates to it.
 *
 * L6 — back-navigation with scroll restoration. The user drill-down is a
 * distinct page (not a modal), so navigating back used to drop the caller
 * at the top of the source page. We now stash `{ returnTo, scrollY }`
 * inside `location.state` so the drill-down's "← Back" button can
 * `navigate(returnTo, { state: { restoreScrollY } })` and the caller page
 * can restore the scroll offset via a `useLayoutEffect` (see
 * `UserDrilldownPage`).
 *
 * Contract:
 *   - `openUserDetail(id)` — `navigate('/user/<encoded-id>')`, preserving
 *     the current FilterBar search string so the drill-down inherits the
 *     same time range / cohort / etc. Stores current pathname+search +
 *     scrollY in `location.state.returnTo / returnScrollY`.
 *   - `userId` — kept for backwards compatibility with `<UserDetail />`
 *     (still mounted once in App.tsx as a fallback for deep-linked
 *     `?userDetail=<id>` URLs). Reads the query param directly.
 *   - `closeUserDetail()` — removes the query param; kept for the modal's
 *     own close button.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** URL param name — kept as a module constant so test code can import it. */
export const USER_DETAIL_PARAM = 'userDetail';

/**
 * Shape stashed in `location.state` when opening the drill-down. The
 * drill-down's back button looks for these keys; missing state means the
 * user deep-linked or refreshed — in which case "← Back" falls back to
 * the browser history.
 */
export interface UserDrilldownLocationState {
  /** `pathname + search` of the page that opened the drill-down. */
  returnTo?: string;
  /** Viewport scroll position on the source page at navigation time. */
  returnScrollY?: number;
}

/**
 * Shape of `location.state` when the drill-down hands control back to the
 * source page. The source page's root component restores the scroll on
 * mount if this key is set.
 */
export interface RestoreScrollLocationState {
  /** Viewport scroll offset to restore after navigation. */
  restoreScrollY?: number;
}

export interface UseOpenUserDetailResult {
  /** Current user id from the legacy `?userDetail=<id>` query param, or null. */
  userId: string | null;
  /** Navigate to `/user/<id>` (fullscreen drill-down with charts). */
  openUserDetail: (id: string) => void;
  /** Remove the legacy `?userDetail` param; closes the fallback modal. */
  closeUserDetail: () => void;
}

export function useOpenUserDetail(): UseOpenUserDetailResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const userId = searchParams.get(USER_DETAIL_PARAM);

  const openUserDetail = useCallback(
    (id: string): void => {
      // L5 — route to the fullscreen drill-down instead of the cropped
      // dialog. Preserve the current search string (filters, granularity,
      // sort) so the drill-down hydrates against the same window the
      // caller was looking at. `encodeURIComponent` handles slack ids that
      // contain `.` or punctuation safely.
      //
      // L6 — capture scroll position + return path so the drill-down's
      // "← Back" can hand control back to exactly where the user was. We
      // guard `window` access for SSR / test environments.
      const scrollY =
        typeof window !== 'undefined' ? window.scrollY : 0;
      const returnTo = `${pathname}${search}`;
      const state: UserDrilldownLocationState = {
        returnTo,
        returnScrollY: scrollY,
      };
      navigate(
        {
          pathname: `/user/${encodeURIComponent(id)}`,
          search,
        },
        { state },
      );
    },
    [navigate, pathname, search],
  );

  const closeUserDetail = useCallback((): void => {
    const next = new URLSearchParams(searchParams);
    next.delete(USER_DETAIL_PARAM);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  return { userId: userId && userId.length > 0 ? userId : null, openUserDetail, closeUserDetail };
}
